/**
 * หน้าจอ Outsourcing (P2-8g, D-103) — ส่งวัตถุดิบ/งานระหว่างผลิตไปให้ผู้รับจ้าง
 * ช่วง (subcontractor) รับกลับ และตัดบัญชีส่วนที่ขาด
 *
 * โครงยืมมาจาก `PackingScreen`/`DeliveryScreen` โดยตั้งใจ ("COPY THEIR SHAPE, NOT
 * THEIR FILES"): ช่องกรองบนสุด ตารางรายการ ปุ่ม lifecycle รายแถว ฟอร์มอยู่หลัง
 * ปุ่มในกล่อง `Modal` และหลังสำเร็จก็โหลดรายการใหม่ผ่าน client เส้นเดิม —
 * `lib/packing/**`, `lib/deliveries/**` และ `desktop/sales/**` เป็น forbidden_scope
 * ของ Work Order นี้ ไฟล์นี้และ `lib/outsourcing/**` จึงเป็น feature folder ใหม่
 * ทั้งชุด ไม่ import จากฝั่งนั้นเลย
 *
 * สามความต่างที่ invariant ของ Work Order นี้เขียนไว้ตรง ๆ:
 *
 * 1. **THE NOT-A-SALE HEADLINE** — ผลของการ dispatch แสดงเป็นการย้ายระหว่างที่เก็บ
 *    ของโรงงานเดียวกัน ไม่มีเอกสารบัญชี และหน้าตาต้อง**ไม่เหมือน**หน้าจอ Delivery
 *    ที่ของออกจากบริษัทจริง (D-103 ข้อ 1)
 * 2. **THE SHORTAGE HEADLINE** — การรับกลับครบกับรับกลับขาดต้องเรนเดอร์ต่างกัน
 *    ชัดเจน แถวที่ขาดโชว์ทั้งการตัดบัญชีและ discrepancy ที่มันก่อ (D-103 ข้อ 10g)
 * 3. **THE QUALITY RULE** — ล็อตที่ `QUALITY_HOLD`/`BLOCKED`/`REJECTED` ส่งออกได้
 *    ปกติ ไม่มีช่องกรอกหรือปุ่มใดถูกปิดเพราะสถานะคุณภาพ ตรงข้ามกับ Packing โดยตั้งใจ
 *
 * **THE APPROVAL IS SEPARATE**: การอนุมัติ discrepancy ไม่ได้อยู่ในฟอร์มรับกลับเลย
 * — มันเป็นปุ่มของตัวเองที่โผล่ในแผงผลลัพธ์ของการรับกลับ ใช้สิทธิ์ `APPROVE`
 * ต่างจากการรับกลับเองที่ใช้ `EDIT` — ตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ทีละเส้น
 *
 * การ gate สิทธิ์: `OUTSOURCING` + `VIEW` เป็นสิทธิ์ของ**การเปิดหน้าจอนี้** (ด่าน
 * อยู่ที่ route) ส่วน `CREATE` (เปิดใบใหม่), `EDIT` (เพิ่มบรรทัด/ส่งออก/รับกลับ)
 * และ `APPROVE` (ยืนยันพร้อมส่งออก/อนุมัติ discrepancy) เป็นสิทธิ์ของ**ปุ่มที่เขียน**
 * ซึ่งถูกบังคับข้างในนี้ทีละปุ่ม ตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ทีละเส้น
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก
 * request เสมอ (`references/authorization-rbac.md` ข้อ 5)
 */

import { useEffect, useState } from 'react';

import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { Modal } from '../components/Modal';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  addOutsourceOrderLine,
  approveOutsourceReturnDiscrepancy,
  createOutsourceOrder,
  dispatchOutsourceOrder,
  fetchOutsourceOrders,
  markOutsourceOrderReady,
  newIdempotencyKey,
  outsourceReturnSubmissionFor,
  receiveOutsourceReturn,
  ruleFromFailure,
  type OutsourceFailure,
  type OutsourceReturnSubmission,
} from '../../lib/outsourcing/client';
import {
  EMPTY_ADD_OUTSOURCE_ORDER_LINE_FIELDS,
  EMPTY_CREATE_OUTSOURCE_ORDER_FIELDS,
  EMPTY_DISPATCH_PACKAGE_DRAFT,
  EMPTY_RETURN_PACKAGE_DRAFT,
  canAddOutsourceOrderLine,
  canDispatchOutsourceOrder,
  canMarkOutsourceOrderReady,
  canReceiveOutsourceReturn,
  dispatchPackageQualityNote,
  isDispatchLotQualityHint,
  outsourceDiscrepancyIsApprovable,
  outsourceDiscrepancyStatusLabel,
  outsourceDiscrepancyStatusTone,
  outsourceOrderHasNoLines,
  outsourceOrderStatusLabel,
  outsourceOrderStatusTone,
  outsourceReturnLineIsShort,
  toAddOutsourceOrderLineBody,
  toCreateOutsourceOrderBody,
  toDispatchOutsourceOrderBody,
  toOutsourcePlantCodeFilter,
  toOutsourceStatusFilter,
  toReceiveOutsourceReturnBody,
  type AddOutsourceOrderLineFields,
  type CreateOutsourceOrderFields,
  type DispatchPackageDraft,
  type ReturnPackageDraft,
} from '../../lib/outsourcing/outsourcingView';
import {
  APPROVAL_AUTHORITY_BELOW_MANAGER,
  APPROVER_MUST_DIFFER_FROM_RAISER,
  DESTINATION_MUST_BE_A_SUBCONTRACTOR_LOCATION,
  DESTINATION_SUPPLIER_MUST_MATCH_ORDER,
  DISPATCH_HAS_NO_PACKAGES,
  IDEMPOTENCY_KEY_REQUIRED,
  IDEMPOTENCY_KEY_REUSED,
  INSUFFICIENT_STOCK_AT_SOURCE,
  LINE_NOT_FOUND,
  LINE_PLANT_MUST_MATCH_ORDER,
  LOT_NOT_FOUND,
  LOT_PLANT_MUST_MATCH_ORDER,
  NO_ACCOUNT_MAPPING,
  NO_UNIT_COST,
  OPERATION_ALREADY_ON_THIS_ORDER,
  OPERATION_NOT_FOUND,
  OUTSOURCE_ORDER_HAS_NO_LINES,
  OUTSOURCE_ORDER_NOT_DISPATCHED,
  OUTSOURCE_ORDER_NOT_READY_TO_DISPATCH,
  OUTSOURCE_PROCESS_NOT_ACTIVE,
  OUTSOURCE_PROCESS_NOT_FOUND,
  OUTSOURCE_RETURN_DISCREPANCY_NOT_APPROVABLE,
  OUTSOURCING_MODULE,
  PLANT_CODE_MAX_LENGTH,
  PLANT_OUTSIDE_SCOPE,
  PRODUCTION_ORDER_NOT_FOUND,
  PRODUCTION_ORDER_NO_MAX_LENGTH,
  QUANTITY_MUST_BE_POSITIVE,
  READY_BY_MUST_DIFFER_FROM_CREATOR,
  RECEIVING_LOCATION_MUST_NOT_BE_A_SUBCONTRACTOR_LOCATION,
  REASON_WITHOUT_A_VARIANCE,
  RETURN_EXCEEDS_TAG,
  RETURN_HAS_NO_PACKAGES,
  RETURN_LINE_RECORDS_NOTHING,
  SOURCE_MUST_DIFFER_FROM_DESTINATION,
  STORAGE_LOCATION_CODE_MAX_LENGTH,
  STORAGE_LOCATION_NOT_FOUND,
  SUPPLIER_CODE_MAX_LENGTH,
  SUPPLIER_IS_BLOCKED,
  SUPPLIER_NOT_FOUND,
  TAG_ALREADY_SCANNED_ON_THIS_RETURN,
  TAG_NOT_ON_THIS_ORDER,
  TAG_QUANTITY_EXCEEDS_LINE,
  UOM_CODE_MAX_LENGTH,
  UOM_NOT_FOUND,
  VARIANCE_NEEDS_A_REASON,
  OUTSOURCE_ORDER_STATUSES,
  OUTSOURCE_PROCESS_CODE_MAX_LENGTH,
  NOTE_MAX_LENGTH,
  TAG_CODE_MAX_LENGTH,
  DISPATCH_LOT_QUALITY_HINTS,
  type OutsourceDispatchResult,
  type OutsourceOrder,
  type OutsourceReturnResult,
} from '../../lib/outsourcing/types';

/* ------------------------------------------------- ความล้มเหลว ----- */

type FailureContext = 'list' | 'create' | 'add-line' | 'ready' | 'dispatch' | 'receive' | 'approve';

interface FailureCopy {
  readonly heading: string;
  readonly text: string;
}

function nothingChangedText(context: FailureContext): string {
  switch (context) {
    case 'create':
      return ' ไม่มีใบใดถูกเปิด และไม่มีแถว audit ถูกเขียน';
    case 'add-line':
      return ' ไม่มีบรรทัดใดถูกเพิ่ม';
    case 'ready':
      return ' ใบนี้ยังอยู่ที่ร่างเหมือนเดิม ไม่มีสต๊อกเคลื่อน';
    case 'dispatch':
      return ' ไม่มีอะไรออกจากที่เก็บ ไม่มี inv_ledger ถูกเขียน — การส่งออกเป็นธุรกรรมเดียว';
    case 'receive':
      return ' ไม่มีอะไรถูกรับกลับ ไม่มี inv_ledger ถูกเขียน และไม่มีการตัดบัญชี — การรับกลับเป็นธุรกรรมเดียว';
    case 'approve':
      return ' รายการนี้ยังไม่ถูกอนุมัติ (ตัวเลขที่ตัดบัญชีไปแล้วตอนรับกลับไม่เปลี่ยนไม่ว่าผลของการอนุมัตินี้จะเป็นอย่างไร)';
    case 'list':
      return '';
  }
}

function isWriteContext(context: FailureContext): boolean {
  return nothingChangedText(context) !== '';
}

function ruleCopy(rule: string | null): FailureCopy | null {
  switch (rule) {
    case PLANT_OUTSIDE_SCOPE:
      return {
        heading: 'โรงงานที่ระบุอยู่นอกขอบเขตของคุณ',
        text: `เซิร์ฟเวอร์ตอบ 403 พร้อมกฎ ${PLANT_OUTSIDE_SCOPE} — การเปิดใบ/ส่งออก/รับกลับเป็นการเขียนเข้าโรงงานหนึ่ง (D-004)`,
      };
    case SUPPLIER_NOT_FOUND:
      return { heading: 'ไม่พบผู้ขายรหัสนี้', text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${SUPPLIER_NOT_FOUND}` };
    case SUPPLIER_IS_BLOCKED:
      return {
        heading: 'ผู้ขายรายนี้ถูกระงับ',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${SUPPLIER_IS_BLOCKED} — ผู้ขายที่ถูกระงับรับงานจ้างผลิตไม่ได้ (D-083)`,
      };
    case OUTSOURCE_PROCESS_NOT_FOUND:
      return { heading: 'ไม่พบกระบวนการรหัสนี้', text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${OUTSOURCE_PROCESS_NOT_FOUND}` };
    case OUTSOURCE_PROCESS_NOT_ACTIVE:
      return {
        heading: 'กระบวนการนี้ถูกถอนออกจากทะเบียนแล้ว',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${OUTSOURCE_PROCESS_NOT_ACTIVE}`,
      };
    case UOM_NOT_FOUND:
      return { heading: 'ไม่พบหน่วยนับรหัสนี้', text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${UOM_NOT_FOUND}` };
    case PRODUCTION_ORDER_NOT_FOUND:
      return { heading: 'ไม่พบใบสั่งผลิตเลขนี้', text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${PRODUCTION_ORDER_NOT_FOUND}` };
    case LINE_PLANT_MUST_MATCH_ORDER:
      return {
        heading: 'ใบสั่งผลิตนี้อยู่คนละโรงงานกับใบจ้างผลิตภายนอก',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${LINE_PLANT_MUST_MATCH_ORDER}`,
      };
    case OPERATION_NOT_FOUND:
      return { heading: 'ไม่พบลำดับ operation นี้ในใบสั่งผลิต', text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${OPERATION_NOT_FOUND}` };
    case OPERATION_ALREADY_ON_THIS_ORDER:
      return {
        heading: 'operation นี้ถูกเพิ่มเข้าใบนี้ไปแล้ว',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${OPERATION_ALREADY_ON_THIS_ORDER} — หนึ่ง operation ไปได้แค่หนึ่งบรรทัด (D-103 ข้อ 7)`,
      };
    case QUANTITY_MUST_BE_POSITIVE:
      return { heading: 'จำนวนที่กรอกต้องมากกว่าศูนย์', text: `เซิร์ฟเวอร์ตอบ 400 พร้อมกฎ ${QUANTITY_MUST_BE_POSITIVE}` };
    case OUTSOURCE_ORDER_HAS_NO_LINES:
      return {
        heading: 'ใบนี้ยังไม่มีบรรทัดเลย',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${OUTSOURCE_ORDER_HAS_NO_LINES} — เพิ่มอย่างน้อยหนึ่งบรรทัดก่อนยืนยันพร้อมส่งออก`,
      };
    case READY_BY_MUST_DIFFER_FROM_CREATOR:
      return {
        heading: 'ผู้ยืนยันต้องไม่ใช่ผู้เปิดใบ',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${READY_BY_MUST_DIFFER_FROM_CREATOR} — แบ่งแยกหน้าที่ (D-010) แม้ผู้กดจะถือสิทธิ์ APPROVE ก็ตาม`,
      };
    case OUTSOURCE_ORDER_NOT_READY_TO_DISPATCH:
      return {
        heading: 'ใบนี้ยังไม่พร้อมส่งออก',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${OUTSOURCE_ORDER_NOT_READY_TO_DISPATCH} — ต้องผ่าน "ยืนยันพร้อมส่งออก" ก่อน`,
      };
    case DISPATCH_HAS_NO_PACKAGES:
      return { heading: 'ยังไม่ได้ระบุแพ็กเกจที่จะส่งออกเลย', text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${DISPATCH_HAS_NO_PACKAGES}` };
    case STORAGE_LOCATION_NOT_FOUND:
      return { heading: 'ไม่พบที่เก็บรหัสนี้ในโรงงานนี้', text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${STORAGE_LOCATION_NOT_FOUND}` };
    case DESTINATION_MUST_BE_A_SUBCONTRACTOR_LOCATION:
      return {
        heading: 'ที่เก็บปลายทางไม่ใช่ที่เก็บของผู้รับจ้างช่วง',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${DESTINATION_MUST_BE_A_SUBCONTRACTOR_LOCATION}`,
      };
    case DESTINATION_SUPPLIER_MUST_MATCH_ORDER:
      return {
        heading: 'ที่เก็บปลายทางเป็นของผู้รับจ้างช่วงรายอื่น',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${DESTINATION_SUPPLIER_MUST_MATCH_ORDER} — ผู้ขายของทั้งสองฝั่งอยู่ในรายละเอียดด้านล่าง`,
      };
    case NO_ACCOUNT_MAPPING:
      return { heading: 'ยังไม่มีการตั้งค่าบัญชีสำหรับรายการนี้', text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${NO_ACCOUNT_MAPPING}` };
    case LOT_NOT_FOUND:
      return { heading: 'ไม่พบล็อตรหัสนี้', text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${LOT_NOT_FOUND}` };
    case LOT_PLANT_MUST_MATCH_ORDER:
      return { heading: 'ล็อตนี้เป็นของอีกโรงงานหนึ่ง', text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${LOT_PLANT_MUST_MATCH_ORDER}` };
    case LINE_NOT_FOUND:
      return { heading: 'ใบนี้ไม่มีเลขบรรทัดที่ระบุ', text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${LINE_NOT_FOUND}` };
    case SOURCE_MUST_DIFFER_FROM_DESTINATION:
      return {
        heading: 'ที่เก็บต้นทางเป็นที่เดียวกับปลายทาง',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${SOURCE_MUST_DIFFER_FROM_DESTINATION}`,
      };
    case TAG_QUANTITY_EXCEEDS_LINE:
      return {
        heading: 'จำนวนที่แท็กรวมกันเกินจำนวนของบรรทัด',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${TAG_QUANTITY_EXCEEDS_LINE} — ตัวเลขของบรรทัดและที่แท็กไปแล้วอยู่ด้านล่าง`,
      };
    case INSUFFICIENT_STOCK_AT_SOURCE:
      return {
        heading: 'ที่เก็บต้นทางมีล็อตนี้ไม่พอ',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${INSUFFICIENT_STOCK_AT_SOURCE}`,
      };
    case OUTSOURCE_ORDER_NOT_DISPATCHED:
      return {
        heading: 'ใบนี้ยังไม่ถูกส่งออก จึงยังรับกลับไม่ได้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${OUTSOURCE_ORDER_NOT_DISPATCHED} — สถานะปัจจุบันอยู่ในรายละเอียดด้านล่าง`,
      };
    case RETURN_HAS_NO_PACKAGES:
      return { heading: 'ยังไม่ได้สแกนแท็กที่รับกลับเลย', text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${RETURN_HAS_NO_PACKAGES}` };
    case RECEIVING_LOCATION_MUST_NOT_BE_A_SUBCONTRACTOR_LOCATION:
      return {
        heading: 'ที่เก็บรับกลับต้องไม่ใช่ที่เก็บของผู้รับจ้างช่วง',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${RECEIVING_LOCATION_MUST_NOT_BE_A_SUBCONTRACTOR_LOCATION}`,
      };
    case TAG_NOT_ON_THIS_ORDER:
      return { heading: 'แท็กนี้ไม่ได้อยู่ในใบนี้', text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${TAG_NOT_ON_THIS_ORDER}` };
    case TAG_ALREADY_SCANNED_ON_THIS_RETURN:
      return {
        heading: 'แท็กนี้ถูกสแกนซ้ำในคำขอเดียวกัน',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${TAG_ALREADY_SCANNED_ON_THIS_RETURN} — ลบแถวที่ซ้ำออกหนึ่งแถว`,
      };
    case RETURN_LINE_RECORDS_NOTHING:
      return {
        heading: 'แถวนี้ไม่ได้บันทึกทั้งจำนวนที่รับและส่วนต่าง',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${RETURN_LINE_RECORDS_NOTHING}`,
      };
    case VARIANCE_NEEDS_A_REASON:
      return {
        heading: 'มีส่วนต่างแต่ยังไม่ได้ระบุเหตุผล',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${VARIANCE_NEEDS_A_REASON}`,
      };
    case REASON_WITHOUT_A_VARIANCE:
      return {
        heading: 'ระบุเหตุผลไว้แต่ส่วนต่างเป็นศูนย์',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${REASON_WITHOUT_A_VARIANCE}`,
      };
    case RETURN_EXCEEDS_TAG:
      return {
        heading: 'จำนวนที่รับกลับเกินกว่าที่แท็กนี้ส่งออกไป',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${RETURN_EXCEEDS_TAG} — นับรวมทุกครั้งที่เคยรับกลับของแท็กนี้แล้ว`,
      };
    case NO_UNIT_COST:
      return { heading: 'สินค้านี้ยังไม่มีต้นทุนต่อหน่วย', text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${NO_UNIT_COST} — ตัดบัญชีส่วนขาดไม่ได้จนกว่าจะมีต้นทุน` };
    case IDEMPOTENCY_KEY_REQUIRED:
      return {
        heading: 'คำขอนี้ขาด Idempotency-Key',
        text: `เซิร์ฟเวอร์ตอบ 400 พร้อมกฎ ${IDEMPOTENCY_KEY_REQUIRED} — นี่ไม่ควรเกิดจากหน้าจอนี้ แจ้งผู้ดูแลระบบ`,
      };
    case IDEMPOTENCY_KEY_REUSED:
      return {
        heading: 'คีย์นี้เคยใช้กับคำขอที่เนื้อหาต่างออกไปแล้ว',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${IDEMPOTENCY_KEY_REUSED} — โหลดรายการใหม่แล้วลองบันทึกการรับกลับอีกครั้ง`,
      };
    case APPROVAL_AUTHORITY_BELOW_MANAGER:
      return {
        heading: 'สิทธิ์อนุมัติของคุณยังไม่ถึงระดับผู้จัดการ',
        text: `เซิร์ฟเวอร์ตอบ 403 พร้อมกฎ ${APPROVAL_AUTHORITY_BELOW_MANAGER} — การอนุมัติส่วนขาดต้องมี authority_level ระดับ MANAGER ขึ้นไป ไม่ใช่แค่สิทธิ์โมดูล`,
      };
    case OUTSOURCE_RETURN_DISCREPANCY_NOT_APPROVABLE:
      return {
        heading: 'รายการนี้อนุมัติไม่ได้แล้ว',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${OUTSOURCE_RETURN_DISCREPANCY_NOT_APPROVABLE} — สถานะปัจจุบันอยู่ในรายละเอียดด้านล่าง`,
      };
    case APPROVER_MUST_DIFFER_FROM_RAISER:
      return {
        heading: 'ผู้อนุมัติต้องไม่ใช่ผู้บันทึกส่วนขาดคนเดียวกัน',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${APPROVER_MUST_DIFFER_FROM_RAISER} — แบ่งแยกหน้าที่ (D-010)`,
      };
    default:
      return null;
  }
}

function failureCopy(failure: OutsourceFailure, context: FailureContext): FailureCopy {
  const writing = isWriteContext(context);
  const nothingChanged = nothingChangedText(context);

  switch (failure.status) {
    case 'unauthenticated':
      return { heading: 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้', text: 'เซิร์ฟเวอร์ตอบ 401 — เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว' };
    case 'forbidden':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'สิทธิ์ชุดนี้ไม่ครอบคลุมคำขอนี้',
          text: `เซิร์ฟเวอร์ตอบ 403 — คำขอนี้ต้องการโมดูล ${OUTSOURCING_MODULE} ในระดับที่สูงกว่าที่แผนกของคุณถืออยู่${nothingChanged}`,
        }
      );
    case 'not-found':
      return {
        heading: 'ไม่พบรายการที่คำขอนี้อ้างถึง',
        text: `เซิร์ฟเวอร์ตอบ 404 — รายการนี้ไม่มีอยู่แล้ว หรืออยู่ในโรงงานนอกขอบเขตของคุณ โหลดรายการใหม่แล้วลองอีกครั้ง${nothingChanged}`,
      };
    case 'conflict':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'สถานะปัจจุบันของรายการนี้ไม่รับสิ่งที่กด',
          text: `เซิร์ฟเวอร์ตอบ 409 — โหลดรายการใหม่เพื่อดูสถานะจริงก่อนลองอีกครั้ง${nothingChanged}`,
        }
      );
    case 'rule-violation':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'คำขอนี้ติดกฎทางธุรกิจ',
          text: `เซิร์ฟเวอร์ตอบ 422 พร้อมรหัสกฎที่ชน — ไม่ใช่ข้อผิดพลาดของระบบ${nothingChanged}`,
        }
      );
    case 'bad-request':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง',
          text: `เซิร์ฟเวอร์ตอบ 400 — ข้อมูลที่ส่งไปไม่ตรงรูปแบบที่ API รับ${nothingChanged}`,
        }
      );
    case 'server-error':
      return {
        heading: `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`,
        text: writing
          ? 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ และไม่มีอะไรยืนยันว่าข้อมูลถูกเปลี่ยนหรือไม่ — โหลดรายการใหม่ก่อนลองอีกครั้ง'
          : 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ',
      };
    case 'network-error':
      return {
        heading: 'ติดต่อเซิร์ฟเวอร์ไม่ได้',
        text: writing
          ? `${failure.detail} — ไม่มีอะไรยืนยันว่าคำขอไปถึงเซิร์ฟเวอร์หรือไม่ โหลดรายการใหม่ก่อนลองอีกครั้ง`
          : `${failure.detail} — หน้าจอนี้จึงไม่มีข้อมูลจะแสดง`,
      };
    case 'invalid-response':
      return {
        heading: 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ',
        text: 'หน้าจอปฏิเสธที่จะแสดงรายการที่อ่านได้ไม่ครบ',
      };
    case 'cancelled':
      return { heading: 'ยกเลิกการเรียกข้อมูลแล้ว', text: 'คำขอถูกยกเลิกก่อนได้รับคำตอบ' };
  }
}

function detailEntries(failure: OutsourceFailure): readonly (readonly [string, string])[] {
  if (!('error' in failure)) {
    return [];
  }
  return Object.entries(failure.error.details)
    .filter(([key]) => key !== 'rule')
    .map(([key, value]) => [key, formatDetailValue(value)] as const);
}

function formatDetailValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map((entry) => formatDetailValue(entry)).join(', ');
  }
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

interface FailureNoticeProps {
  readonly failure: OutsourceFailure;
  readonly context: FailureContext;
  readonly testIdPrefix: string;
  readonly onRetry?: () => void;
}

function OutsourceFailureNotice({ failure, context, testIdPrefix, onRetry }: FailureNoticeProps) {
  const copy = failureCopy(failure, context);
  const rule = ruleFromFailure(failure);
  const serverMessage = 'error' in failure ? failure.error.message : null;
  const entries = detailEntries(failure);
  const refusedByRule = failure.status === 'rule-violation' || failure.status === 'conflict';

  return (
    <div
      className="cap-notice"
      data-testid={`${testIdPrefix}-failure`}
      data-failure={failure.status}
      data-context={context}
      data-rule={rule ?? ''}
      role="status"
    >
      <StatusBadge tone={refusedByRule ? 'warning' : 'danger'}>
        {refusedByRule ? 'ติดกฎทางธุรกิจ' : 'ทำรายการไม่สำเร็จ'}
      </StatusBadge>
      <h3 className="cap-notice__title">{copy.heading}</h3>
      <p className="cap-notice__text">{copy.text}</p>
      {rule === null ? null : (
        <p className="cap-notice__text">
          กฎที่ชน: <code className="cap-table__code" data-testid={`${testIdPrefix}-rule`}>{rule}</code>
        </p>
      )}
      {serverMessage === null ? null : (
        <p className="cap-notice__note" data-testid={`${testIdPrefix}-failure-message`}>
          ข้อความจากเซิร์ฟเวอร์: {serverMessage}
        </p>
      )}
      {entries.length === 0 ? null : (
        <dl className="cap-notice__details" data-testid={`${testIdPrefix}-failure-details`}>
          {entries.map(([key, value]) => (
            <div key={key} data-testid={`${testIdPrefix}-failure-detail-${key}`}>
              <dt>{key}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      )}
      {onRetry === undefined ? null : (
        <div className="cap-notice__actions">
          <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
            ลองอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- หน้าจอ -- */

export interface OutsourceOrderScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
}

type ListState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly OutsourceOrder[] }
  | OutsourceFailure;

type ActiveModal =
  | { readonly kind: 'create' }
  | { readonly kind: 'add-line'; readonly order: OutsourceOrder }
  | { readonly kind: 'dispatch'; readonly order: OutsourceOrder }
  | { readonly kind: 'receive'; readonly order: OutsourceOrder };

interface DispatchPackageRow {
  readonly key: number;
  readonly draft: DispatchPackageDraft;
}

interface ReturnPackageRow {
  readonly key: number;
  readonly draft: ReturnPackageDraft;
}

function newDispatchRow(key: number): DispatchPackageRow {
  return { key, draft: EMPTY_DISPATCH_PACKAGE_DRAFT };
}

function newReturnRow(key: number): ReturnPackageRow {
  return { key, draft: EMPTY_RETURN_PACKAGE_DRAFT };
}

export function OutsourceOrderScreen({ permissions, apiBaseUrl, fetchImpl }: OutsourceOrderScreenProps) {
  const canCreate = can(permissions, OUTSOURCING_MODULE, 'CREATE');
  const canEdit = can(permissions, OUTSOURCING_MODULE, 'EDIT');
  const canApprove = can(permissions, OUTSOURCING_MODULE, 'APPROVE');
  const canWriteAnything = canCreate || canEdit || canApprove;

  const [items, setItems] = useState<ListState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [busyOrderId, setBusyOrderId] = useState<string | null>(null);

  const [plantCodeInput, setPlantCodeInput] = useState('');
  const [plantCodeFilter, setPlantCodeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [modal, setModal] = useState<ActiveModal | null>(null);

  const [createFields, setCreateFields] = useState<CreateOutsourceOrderFields>(
    EMPTY_CREATE_OUTSOURCE_ORDER_FIELDS,
  );
  const [createFailure, setCreateFailure] = useState<OutsourceFailure | null>(null);

  const [lineFields, setLineFields] = useState<AddOutsourceOrderLineFields>(
    EMPTY_ADD_OUTSOURCE_ORDER_LINE_FIELDS,
  );
  const [lineFailure, setLineFailure] = useState<OutsourceFailure | null>(null);

  const [readyFailure, setReadyFailure] = useState<{ readonly orderId: string; readonly failure: OutsourceFailure } | null>(
    null,
  );

  const [destinationLocationCode, setDestinationLocationCode] = useState('');
  const [dispatchRows, setDispatchRows] = useState<readonly DispatchPackageRow[]>([newDispatchRow(0)]);
  const [nextDispatchKey, setNextDispatchKey] = useState(1);
  const [dispatchFailure, setDispatchFailure] = useState<OutsourceFailure | null>(null);
  const [dispatchResult, setDispatchResult] = useState<OutsourceDispatchResult | null>(null);

  const [receivingLocationCode, setReceivingLocationCode] = useState('');
  const [receiveNote, setReceiveNote] = useState('');
  const [returnRows, setReturnRows] = useState<readonly ReturnPackageRow[]>([newReturnRow(0)]);
  const [nextReturnKey, setNextReturnKey] = useState(1);
  const [receiveFailure, setReceiveFailure] = useState<OutsourceFailure | null>(null);
  const [receiveSubmission, setReceiveSubmission] = useState<OutsourceReturnSubmission | null>(null);
  const [receiveResult, setReceiveResult] = useState<OutsourceReturnResult | null>(null);

  const [approvePending, setApprovePending] = useState<string | null>(null);
  const [approveFailure, setApproveFailure] = useState<{ readonly discrepancyId: string; readonly failure: OutsourceFailure } | null>(
    null,
  );

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setItems((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));

    void fetchOutsourceOrders(
      {
        ...(plantCodeFilter === '' ? {} : { plant_code: plantCodeFilter }),
        ...(statusFilter === '' ? {} : { status: statusFilter }),
      },
      { apiBaseUrl, fetchImpl, signal: controller.signal },
    ).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setItems(result.status === 'ready' ? { status: 'ready', items: result.page.items } : result);
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, reloadToken, plantCodeFilter, statusFilter]);

  const busy = busyOrderId !== null;

  /* ------------------------------------------------------------ สร้างใบ -- */

  const openCreate = () => {
    if (!canCreate) {
      return;
    }
    setCreateFields(EMPTY_CREATE_OUTSOURCE_ORDER_FIELDS);
    setCreateFailure(null);
    setModal({ kind: 'create' });
  };

  const closeModal = () => {
    setModal(null);
  };

  const createBody = toCreateOutsourceOrderBody(createFields);

  const submitCreate = async () => {
    if (createBody === null || busy || !canCreate) {
      return;
    }
    setBusyOrderId('__create__');
    setCreateFailure(null);
    const result = await createOutsourceOrder(createBody, { apiBaseUrl, fetchImpl });
    setBusyOrderId(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setCreateFailure(result);
      return;
    }
    setModal(null);
    setReloadToken((token) => token + 1);
  };

  /* --------------------------------------------------------- เพิ่มบรรทัด -- */

  const openAddLine = (order: OutsourceOrder) => {
    if (!canEdit) {
      return;
    }
    setLineFields(EMPTY_ADD_OUTSOURCE_ORDER_LINE_FIELDS);
    setLineFailure(null);
    setModal({ kind: 'add-line', order });
  };

  const lineBody = toAddOutsourceOrderLineBody(lineFields);

  const submitAddLine = async () => {
    if (modal?.kind !== 'add-line' || lineBody === null || busy || !canEdit) {
      return;
    }
    const orderId = modal.order.outsource_order_id;
    setBusyOrderId(orderId);
    setLineFailure(null);
    const result = await addOutsourceOrderLine(orderId, lineBody, { apiBaseUrl, fetchImpl });
    setBusyOrderId(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setLineFailure(result);
      return;
    }
    setModal(null);
    setReloadToken((token) => token + 1);
  };

  /* -------------------------------------------------------------- ready -- */

  const runMarkReady = async (order: OutsourceOrder) => {
    if (busy || !canApprove || outsourceOrderHasNoLines(order)) {
      return;
    }
    setBusyOrderId(order.outsource_order_id);
    setReadyFailure(null);
    const result = await markOutsourceOrderReady(order.outsource_order_id, { apiBaseUrl, fetchImpl });
    setBusyOrderId(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setReadyFailure({ orderId: order.outsource_order_id, failure: result });
      return;
    }
    setReloadToken((token) => token + 1);
  };

  /* ----------------------------------------------------------- dispatch -- */

  const openDispatch = (order: OutsourceOrder) => {
    if (!canEdit) {
      return;
    }
    setDestinationLocationCode('');
    setDispatchRows([newDispatchRow(nextDispatchKey)]);
    setNextDispatchKey((key) => key + 1);
    setDispatchFailure(null);
    setModal({ kind: 'dispatch', order });
  };

  const addDispatchRow = () => {
    setDispatchRows((previous) => [...previous, newDispatchRow(nextDispatchKey)]);
    setNextDispatchKey((key) => key + 1);
  };

  const removeDispatchRow = (key: number) => {
    setDispatchRows((previous) => previous.filter((row) => row.key !== key));
  };

  const updateDispatchRow = (key: number, field: keyof DispatchPackageDraft, value: string) => {
    setDispatchRows((previous) =>
      previous.map((row) =>
        row.key === key ? { ...row, draft: { ...row.draft, [field]: value } } : row,
      ),
    );
  };

  const dispatchBody = toDispatchOutsourceOrderBody(
    destinationLocationCode,
    dispatchRows.map((row) => row.draft),
  );

  const submitDispatch = async () => {
    if (modal?.kind !== 'dispatch' || dispatchBody === null || busy || !canEdit) {
      return;
    }
    const orderId = modal.order.outsource_order_id;
    setBusyOrderId(orderId);
    setDispatchFailure(null);
    const result = await dispatchOutsourceOrder(orderId, dispatchBody, { apiBaseUrl, fetchImpl });
    setBusyOrderId(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setDispatchFailure(result);
      return;
    }
    setDispatchResult(result.result);
    setModal(null);
    setReloadToken((token) => token + 1);
  };

  /* ------------------------------------------------------------ receive -- */

  const openReceive = (order: OutsourceOrder) => {
    if (!canEdit) {
      return;
    }
    setReceivingLocationCode('');
    setReceiveNote('');
    setReturnRows([newReturnRow(nextReturnKey)]);
    setNextReturnKey((key) => key + 1);
    setReceiveFailure(null);
    setModal({ kind: 'receive', order });
  };

  const addReturnRow = () => {
    setReturnRows((previous) => [...previous, newReturnRow(nextReturnKey)]);
    setNextReturnKey((key) => key + 1);
  };

  const removeReturnRow = (key: number) => {
    setReturnRows((previous) => previous.filter((row) => row.key !== key));
  };

  const updateReturnRow = (key: number, field: keyof ReturnPackageDraft, value: string) => {
    setReturnRows((previous) =>
      previous.map((row) =>
        row.key === key ? { ...row, draft: { ...row.draft, [field]: value } } : row,
      ),
    );
  };

  const receiveBody = toReceiveOutsourceReturnBody(
    receivingLocationCode,
    returnRows.map((row) => row.draft),
    receiveNote,
  );

  const submitReceive = async () => {
    if (modal?.kind !== 'receive' || receiveBody === null || busy || !canEdit) {
      return;
    }
    const orderId = modal.order.outsource_order_id;
    const submission = outsourceReturnSubmissionFor(
      receiveSubmission,
      JSON.stringify(receiveBody),
      newIdempotencyKey,
    );
    setReceiveSubmission(submission);
    setBusyOrderId(orderId);
    setReceiveFailure(null);
    const result = await receiveOutsourceReturn(orderId, receiveBody, submission.key, {
      apiBaseUrl,
      fetchImpl,
    });
    setBusyOrderId(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setReceiveFailure(result);
      return;
    }
    setReceiveResult(result.result);
    setReceiveSubmission(null);
    setModal(null);
    setReloadToken((token) => token + 1);
  };

  /* -------------------------------------------------- อนุมัติ discrepancy -- */

  const runApproveDiscrepancy = async (discrepancyId: string) => {
    if (approvePending !== null || !canApprove) {
      return;
    }
    setApprovePending(discrepancyId);
    setApproveFailure(null);
    const result = await approveOutsourceReturnDiscrepancy(discrepancyId, { apiBaseUrl, fetchImpl });
    setApprovePending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setApproveFailure({ discrepancyId, failure: result });
      return;
    }
    setReceiveResult((previous) => {
      if (previous === null) {
        return previous;
      }
      return {
        ...previous,
        lines: previous.lines.map((line) =>
          line.discrepancy?.discrepancy_id === discrepancyId
            ? { ...line, discrepancy: result.discrepancy }
            : line,
        ),
      };
    });
  };

  const listItems = items.status === 'ready' ? items.items : [];

  return (
    <div className="cap-page" data-testid="outsource-orders">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            จ้างผลิตภายนอก (Outsourcing)
            <InfoHint
              label="เกี่ยวกับหน้านี้"
              text={
                <>
                  ส่งวัตถุดิบ/งานระหว่างผลิตไปให้ผู้รับจ้างช่วง — <strong>ผู้รับจ้างช่วงเป็นที่เก็บของโรงงานเดียวกัน
                  ไม่ใช่บริษัทอื่น</strong> การส่งออกจึงไม่ใช่การขายและไม่โพสต์เอกสารบัญชี ส่วนที่ขาดตอนรับ
                  กลับเท่านั้นที่มีผลทางบัญชี (D-103)
                </>
              }
            />
          </h1>
        </div>
      </div>

      {!canWriteAnything ? (
        <div className="cap-notice" data-testid="outsource-read-only" role="status">
          <StatusBadge tone="warning">ดูได้อย่างเดียว</StatusBadge>
          <h2 className="cap-notice__title">สิทธิ์ชุดนี้ยังเปิด/ส่งออก/รับกลับไม่ได้</h2>
          <p className="cap-notice__text">
            สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag <code className="cap-table__code">can_create</code>,{' '}
            <code className="cap-table__code">can_edit</code> หรือ{' '}
            <code className="cap-table__code">can_approve</code> ของโมดูล{' '}
            <code className="cap-table__code">{OUTSOURCING_MODULE}</code> — รายการและตัวกรองยังใช้ได้ตามปกติ
          </p>
        </div>
      ) : null}

      {/* --------------------------------------------------- ตัวกรอง -- */}

      <form
        className="cap-filters"
        data-testid="outsource-filter-form"
        onSubmit={(event) => {
          event.preventDefault();
          setPlantCodeFilter(toOutsourcePlantCodeFilter(plantCodeInput) ?? '');
        }}
      >
        <label className="cap-field">
          <span className="cap-field__label">รหัสโรงงาน</span>
          <input
            className="cap-input"
            type="text"
            data-testid="outsource-filter-plant"
            value={plantCodeInput}
            maxLength={PLANT_CODE_MAX_LENGTH}
            autoComplete="off"
            onChange={(event) => {
              setPlantCodeInput(event.target.value);
            }}
          />
        </label>

        <button type="submit" className="cap-button cap-button--ghost" data-testid="outsource-filter-apply">
          กรอง
        </button>

        <label className="cap-field">
          <span className="cap-field__label">สถานะ</span>
          <select
            className="cap-input"
            data-testid="outsource-filter-status"
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(toOutsourceStatusFilter(event.target.value) ?? '');
            }}
          >
            <option value="">ทุกสถานะ</option>
            {OUTSOURCE_ORDER_STATUSES.map((status) => (
              <option key={status} value={status}>
                {outsourceOrderStatusLabel(status)}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className="cap-button cap-button--primary"
          data-testid="outsource-create-open"
          disabled={!canCreate}
          title={canCreate ? undefined : `ต้องมีสิทธิ์ ${OUTSOURCING_MODULE} + CREATE จึงจะเปิดใบใหม่ได้`}
          onClick={openCreate}
        >
          เปิดใบใหม่
        </button>
        {canCreate ? null : (
          <span className="cap-field__hint" data-testid="outsource-create-open-reason">
            ต้องมีสิทธิ์ {OUTSOURCING_MODULE} + CREATE จึงจะเปิดใบใหม่ได้
          </span>
        )}
      </form>

      {/* ------------------------------------------------------- สร้างใบ -- */}

      <Modal open={modal?.kind === 'create'} onClose={closeModal} title="เปิดใบจ้างผลิตภายนอกใหม่">
        <form
          className="cap-form"
          data-testid="outsource-create-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submitCreate();
          }}
        >
          <label className="cap-field">
            <span className="cap-field__label">รหัสโรงงาน</span>
            <input
              className="cap-input"
              data-testid="outsource-create-plant"
              value={createFields.plantCode}
              maxLength={PLANT_CODE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                setCreateFields((previous) => ({ ...previous, plantCode: event.target.value }));
              }}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">รหัสผู้ขาย (ผู้รับจ้างช่วง)</span>
            <input
              className="cap-input"
              data-testid="outsource-create-supplier"
              value={createFields.supplierCode}
              maxLength={SUPPLIER_CODE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                setCreateFields((previous) => ({ ...previous, supplierCode: event.target.value }));
              }}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">รหัสกระบวนการ</span>
            <input
              className="cap-input"
              data-testid="outsource-create-process"
              value={createFields.processCode}
              maxLength={OUTSOURCE_PROCESS_CODE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                setCreateFields((previous) => ({ ...previous, processCode: event.target.value }));
              }}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">วันที่คาดว่าจะได้รับกลับ</span>
            <input
              className="cap-input"
              type="date"
              data-testid="outsource-create-expected-return-date"
              value={createFields.expectedReturnDate}
              onChange={(event) => {
                setCreateFields((previous) => ({
                  ...previous,
                  expectedReturnDate: event.target.value,
                }));
              }}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">หมายเหตุ</span>
            <input
              className="cap-input"
              data-testid="outsource-create-note"
              value={createFields.note}
              maxLength={NOTE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                setCreateFields((previous) => ({ ...previous, note: event.target.value }));
              }}
            />
          </label>

          <div className="cap-form__actions">
            <button
              type="submit"
              className="cap-button cap-button--primary"
              data-testid="outsource-create-submit"
              disabled={busy || createBody === null}
            >
              {busyOrderId === '__create__' ? 'กำลังเปิดใบ…' : 'เปิดใบ'}
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="outsource-create-cancel"
              disabled={busy}
              onClick={closeModal}
            >
              ยกเลิก
            </button>
          </div>

          {createFailure === null ? null : (
            <OutsourceFailureNotice failure={createFailure} context="create" testIdPrefix="outsource-create" />
          )}
        </form>
      </Modal>

      {/* --------------------------------------------------- เพิ่มบรรทัด -- */}

      <Modal
        open={modal?.kind === 'add-line'}
        onClose={closeModal}
        title={modal?.kind === 'add-line' ? `เพิ่มบรรทัด — ${modal.order.outsource_order_no}` : 'เพิ่มบรรทัด'}
      >
        <form
          className="cap-form"
          data-testid="outsource-line-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submitAddLine();
          }}
        >
          <label className="cap-field">
            <span className="cap-field__label">เลขใบสั่งผลิตต้นทาง</span>
            <input
              className="cap-input"
              data-testid="outsource-line-order-no"
              value={lineFields.orderNo}
              maxLength={PRODUCTION_ORDER_NO_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                setLineFields((previous) => ({ ...previous, orderNo: event.target.value }));
              }}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">ลำดับ operation</span>
            <input
              className="cap-input"
              data-testid="outsource-line-operation-seq"
              inputMode="numeric"
              value={lineFields.operationSeq}
              autoComplete="off"
              onChange={(event) => {
                setLineFields((previous) => ({ ...previous, operationSeq: event.target.value }));
              }}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">จำนวน</span>
            <input
              className="cap-input"
              data-testid="outsource-line-quantity"
              inputMode="decimal"
              value={lineFields.quantity}
              autoComplete="off"
              onChange={(event) => {
                setLineFields((previous) => ({ ...previous, quantity: event.target.value }));
              }}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">หน่วยนับ</span>
            <input
              className="cap-input"
              data-testid="outsource-line-uom"
              value={lineFields.uomCode}
              maxLength={UOM_CODE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                setLineFields((previous) => ({ ...previous, uomCode: event.target.value }));
              }}
            />
          </label>

          <div className="cap-form__actions">
            <button
              type="submit"
              className="cap-button cap-button--primary"
              data-testid="outsource-line-submit"
              disabled={busy || lineBody === null}
            >
              เพิ่มบรรทัด
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="outsource-line-cancel"
              disabled={busy}
              onClick={closeModal}
            >
              ยกเลิก
            </button>
          </div>

          {lineFailure === null ? null : (
            <OutsourceFailureNotice failure={lineFailure} context="add-line" testIdPrefix="outsource-line" />
          )}
        </form>
      </Modal>

      {/* ------------------------------------------------------- dispatch -- */}

      <Modal
        open={modal?.kind === 'dispatch'}
        onClose={closeModal}
        title={modal?.kind === 'dispatch' ? `ส่งออก — ${modal.order.outsource_order_no}` : 'ส่งออก'}
      >
        <form
          className="cap-form"
          data-testid="outsource-dispatch-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submitDispatch();
          }}
        >
          {/* THE NOT-A-SALE HEADLINE — แสดงตั้งแต่ในฟอร์ม ไม่ใช่แค่หลังส่งสำเร็จ */}
          <div className="cap-notice" data-testid="outsource-dispatch-form-not-a-sale" role="status">
            <StatusBadge tone="info">ไม่ใช่การขาย</StatusBadge>
            <p className="cap-notice__text">
              ผู้รับจ้างช่วงคือที่เก็บของโรงงานคุณเอง ของยังเป็นของบริษัทตลอดเวลาที่อยู่นอกโรงงาน — การ
              ส่งออกจึงเป็นการย้ายที่เก็บ ไม่ใช่การส่งของให้ลูกค้าแบบหน้าจอ Delivery และไม่มีเอกสารบัญชี
              ใดถูกสร้างจากขั้นตอนนี้
            </p>
          </div>

          <label className="cap-field">
            <span className="cap-field__label">ที่เก็บปลายทาง (ผู้รับจ้างช่วง)</span>
            <input
              className="cap-input"
              data-testid="outsource-dispatch-destination"
              value={destinationLocationCode}
              maxLength={STORAGE_LOCATION_CODE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                setDestinationLocationCode(event.target.value);
              }}
            />
          </label>

          <fieldset className="cap-field" data-testid="outsource-dispatch-packages">
            <legend className="cap-field__label">แพ็กเกจที่จะส่งออก</legend>

            <div className="cap-table-wrap">
              <table className="cap-table" data-testid="outsource-dispatch-packages-table">
                <thead>
                  <tr>
                    <th scope="col">เลขบรรทัด</th>
                    <th scope="col">รหัสล็อต</th>
                    <th scope="col">ที่เก็บต้นทาง</th>
                    <th scope="col">จำนวน</th>
                    <th scope="col">สถานะคุณภาพ (บันทึกไว้ดูเฉย ๆ)</th>
                    <th scope="col">ลบ</th>
                  </tr>
                </thead>
                <tbody>
                  {dispatchRows.map((row, index) => {
                    const note = dispatchPackageQualityNote(row.draft.qualityHint);
                    return (
                      <tr key={row.key} data-testid={`outsource-dispatch-row-${index + 1}`}>
                        <td>
                          <input
                            className="cap-input"
                            data-testid={`outsource-dispatch-line-no-${index + 1}`}
                            aria-label={`เลขบรรทัดแถวที่ ${index + 1}`}
                            value={row.draft.lineNo}
                            inputMode="numeric"
                            autoComplete="off"
                            onChange={(event) => {
                              updateDispatchRow(row.key, 'lineNo', event.target.value);
                            }}
                          />
                        </td>
                        <td>
                          <input
                            className="cap-input"
                            data-testid={`outsource-dispatch-lot-${index + 1}`}
                            aria-label={`รหัสล็อตแถวที่ ${index + 1}`}
                            value={row.draft.lotId}
                            autoComplete="off"
                            onChange={(event) => {
                              updateDispatchRow(row.key, 'lotId', event.target.value);
                            }}
                          />
                        </td>
                        <td>
                          <input
                            className="cap-input"
                            data-testid={`outsource-dispatch-source-${index + 1}`}
                            aria-label={`ที่เก็บต้นทางแถวที่ ${index + 1}`}
                            value={row.draft.sourceLocationCode}
                            maxLength={STORAGE_LOCATION_CODE_MAX_LENGTH}
                            autoComplete="off"
                            onChange={(event) => {
                              updateDispatchRow(row.key, 'sourceLocationCode', event.target.value);
                            }}
                          />
                        </td>
                        <td>
                          <input
                            className="cap-input"
                            data-testid={`outsource-dispatch-quantity-${index + 1}`}
                            aria-label={`จำนวนแถวที่ ${index + 1}`}
                            value={row.draft.quantity}
                            inputMode="decimal"
                            autoComplete="off"
                            onChange={(event) => {
                              updateDispatchRow(row.key, 'quantity', event.target.value);
                            }}
                          />
                        </td>
                        <td>
                          {/* THE QUALITY RULE — ตัวเลือกนี้ไม่ส่งไปกับ request และไม่ปิดกั้น
                             อะไรเลยไม่ว่าจะเลือกค่าใด (ดู `dispatchPackageQualityNote`) */}
                          <select
                            className="cap-input"
                            data-testid={`outsource-dispatch-quality-${index + 1}`}
                            aria-label={`สถานะคุณภาพแถวที่ ${index + 1}`}
                            value={row.draft.qualityHint}
                            onChange={(event) => {
                              const value = event.target.value;
                              if (isDispatchLotQualityHint(value)) {
                                updateDispatchRow(row.key, 'qualityHint', value);
                              }
                            }}
                          >
                            {DISPATCH_LOT_QUALITY_HINTS.map((hint) => (
                              <option key={hint} value={hint}>
                                {hint === '' ? '(ไม่ระบุ)' : hint}
                              </option>
                            ))}
                          </select>
                          {note === null ? null : (
                            <p
                              className="cap-field__hint"
                              data-testid={`outsource-dispatch-quality-note-${index + 1}`}
                            >
                              {note}
                            </p>
                          )}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="cap-button cap-button--ghost"
                            data-testid={`outsource-dispatch-row-remove-${index + 1}`}
                            aria-label={`ลบแถวที่ ${index + 1}`}
                            onClick={() => {
                              removeDispatchRow(row.key);
                            }}
                          >
                            ลบแถว
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="outsource-dispatch-row-add"
              onClick={addDispatchRow}
            >
              เพิ่มแถวแพ็กเกจ
            </button>
          </fieldset>

          <div className="cap-form__actions">
            <button
              type="submit"
              className="cap-button cap-button--primary"
              data-testid="outsource-dispatch-submit"
              disabled={busy || dispatchBody === null}
            >
              ส่งออก
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="outsource-dispatch-cancel"
              disabled={busy}
              onClick={closeModal}
            >
              ยกเลิก
            </button>
          </div>

          {dispatchFailure === null ? null : (
            <OutsourceFailureNotice failure={dispatchFailure} context="dispatch" testIdPrefix="outsource-dispatch" />
          )}
        </form>
      </Modal>

      {/* ------------------------------------------------------- receive -- */}

      <Modal
        open={modal?.kind === 'receive'}
        onClose={closeModal}
        title={modal?.kind === 'receive' ? `รับกลับ — ${modal.order.outsource_order_no}` : 'รับกลับ'}
      >
        <form
          className="cap-form"
          data-testid="outsource-receive-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submitReceive();
          }}
        >
          <label className="cap-field">
            <span className="cap-field__label">ที่เก็บที่รับกลับ</span>
            <input
              className="cap-input"
              data-testid="outsource-receive-location"
              value={receivingLocationCode}
              maxLength={STORAGE_LOCATION_CODE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                setReceivingLocationCode(event.target.value);
              }}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">หมายเหตุ</span>
            <input
              className="cap-input"
              data-testid="outsource-receive-note"
              value={receiveNote}
              maxLength={NOTE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                setReceiveNote(event.target.value);
              }}
            />
          </label>

          <fieldset className="cap-field" data-testid="outsource-receive-packages">
            <legend className="cap-field__label">
              แท็กที่สแกนกลับมา
              <InfoHint
                label="เกี่ยวกับตารางนี้"
                text="เว้น &quot;ส่วนต่าง&quot; ว่างไว้ = ยังไม่มาส่วนที่เหลือ ระบุจำนวน = ปิดแพ็กเกจนี้พร้อม
              เหตุผล — ส่วนต่างที่ระบุต้องมีเหตุผลกำกับเสมอ (เซิร์ฟเวอร์บังคับ)"
              />
            </legend>

            <div className="cap-table-wrap">
              <table className="cap-table" data-testid="outsource-receive-packages-table">
                <thead>
                  <tr>
                    <th scope="col">รหัสแท็ก</th>
                    <th scope="col">จำนวนที่รับ</th>
                    <th scope="col">ส่วนต่าง (ขาด)</th>
                    <th scope="col">เหตุผล</th>
                    <th scope="col">ลบ</th>
                  </tr>
                </thead>
                <tbody>
                  {returnRows.map((row, index) => (
                    <tr key={row.key} data-testid={`outsource-receive-row-${index + 1}`}>
                      <td>
                        <input
                          className="cap-input"
                          data-testid={`outsource-receive-tag-${index + 1}`}
                          aria-label={`รหัสแท็กแถวที่ ${index + 1}`}
                          value={row.draft.tagCode}
                          maxLength={TAG_CODE_MAX_LENGTH}
                          autoComplete="off"
                          onChange={(event) => {
                            updateReturnRow(row.key, 'tagCode', event.target.value);
                          }}
                        />
                      </td>
                      <td>
                        <input
                          className="cap-input"
                          data-testid={`outsource-receive-returned-qty-${index + 1}`}
                          aria-label={`จำนวนที่รับแถวที่ ${index + 1}`}
                          value={row.draft.returnedQuantity}
                          inputMode="decimal"
                          autoComplete="off"
                          onChange={(event) => {
                            updateReturnRow(row.key, 'returnedQuantity', event.target.value);
                          }}
                        />
                      </td>
                      <td>
                        <input
                          className="cap-input"
                          data-testid={`outsource-receive-variance-qty-${index + 1}`}
                          aria-label={`ส่วนต่างแถวที่ ${index + 1}`}
                          value={row.draft.varianceQuantity}
                          inputMode="decimal"
                          autoComplete="off"
                          onChange={(event) => {
                            updateReturnRow(row.key, 'varianceQuantity', event.target.value);
                          }}
                        />
                      </td>
                      <td>
                        <input
                          className="cap-input"
                          data-testid={`outsource-receive-variance-reason-${index + 1}`}
                          aria-label={`เหตุผลแถวที่ ${index + 1}`}
                          value={row.draft.varianceReason}
                          maxLength={NOTE_MAX_LENGTH}
                          autoComplete="off"
                          onChange={(event) => {
                            updateReturnRow(row.key, 'varianceReason', event.target.value);
                          }}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="cap-button cap-button--ghost"
                          data-testid={`outsource-receive-row-remove-${index + 1}`}
                          aria-label={`ลบแถวที่ ${index + 1}`}
                          onClick={() => {
                            removeReturnRow(row.key);
                          }}
                        >
                          ลบแถว
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="outsource-receive-row-add"
              onClick={addReturnRow}
            >
              เพิ่มแถวแท็ก
            </button>
          </fieldset>

          <div className="cap-form__actions">
            <button
              type="submit"
              className="cap-button cap-button--primary"
              data-testid="outsource-receive-submit"
              disabled={busy || receiveBody === null}
            >
              บันทึกการรับกลับ
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="outsource-receive-cancel"
              disabled={busy}
              onClick={closeModal}
            >
              ยกเลิก
            </button>
          </div>

          {receiveFailure === null ? null : (
            <OutsourceFailureNotice failure={receiveFailure} context="receive" testIdPrefix="outsource-receive" />
          )}
        </form>
      </Modal>

      {/* --------------------------------------------- ผลของการส่งออกล่าสุด -- */}

      {dispatchResult === null ? null : (
        <section className="cap-card" data-testid="outsource-dispatch-result">
          <StatusBadge tone="info">ไม่ใช่การขาย</StatusBadge>
          <h2 className="cap-notice__title" data-testid="outsource-dispatch-not-a-sale">
            ส่งออกใบ {dispatchResult.order.outsource_order_no} ไปที่{' '}
            {dispatchResult.destination_location_code} แล้ว — ของยังเป็นของโรงงานนี้
          </h2>
          <p className="cap-notice__text">
            ที่เก็บปลายทางเป็นที่เก็บของโรงงานเดียวกัน ไม่มีเอกสารบัญชีถูกสร้างจากการส่งออกนี้เลย —
            ยอดรวมของโรงงานไม่เปลี่ยน มีแต่การกระจายที่เปลี่ยน (D-103 ข้อ 1)
          </p>
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="outsource-dispatch-movements-table">
              <thead>
                <tr>
                  <th scope="col">ที่เก็บ</th>
                  <th scope="col">ล็อต</th>
                  <th scope="col">จำนวน (บวก/ลบ)</th>
                  <th scope="col">ประเภท</th>
                </tr>
              </thead>
              <tbody>
                {dispatchResult.movements.map((movement) => (
                  <tr key={movement.ledger_id} data-testid={`outsource-dispatch-movement-${movement.ledger_id}`}>
                    <td>
                      <code className="cap-table__code">{movement.storage_location_code}</code>
                    </td>
                    <td>
                      <code className="cap-table__code">{movement.lot_id}</code>
                    </td>
                    <td>{movement.qty}</td>
                    <td>{movement.movement_type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* --------------------------------------------- ผลของการรับกลับล่าสุด -- */}

      {receiveResult === null ? null : (
        <section className="cap-card" data-testid="outsource-receive-result">
          <h2 className="cap-notice__title">
            รับกลับ {receiveResult.outsource_return_no} ของใบ {receiveResult.order.outsource_order_no} แล้ว
          </h2>
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="outsource-receive-result-table">
              <thead>
                <tr>
                  <th scope="col">แท็ก</th>
                  <th scope="col">รับจริง</th>
                  <th scope="col">ส่วนต่าง</th>
                  <th scope="col">ผล</th>
                  <th scope="col">การอนุมัติ</th>
                </tr>
              </thead>
              <tbody>
                {receiveResult.lines.map((line) => {
                  const isShort = outsourceReturnLineIsShort(line);
                  const discrepancy = line.discrepancy;
                  return (
                    <tr key={line.tag_code} data-testid={`outsource-return-line-${line.tag_code}`} data-short={isShort}>
                      <td>
                        <code className="cap-table__code">{line.tag_code}</code>
                      </td>
                      <td>{line.returned_quantity}</td>
                      <td>{line.variance_quantity}</td>
                      <td>
                        {isShort ? (
                          <div data-testid={`outsource-return-line-short-${line.tag_code}`}>
                            <StatusBadge tone="danger">ขาด — ตัดบัญชีแล้ว</StatusBadge>
                            <p className="cap-notice__note">
                              บันทึกเป็นค่าใช้จ่ายในธุรกรรมเดียวกับการรับกลับนี้แล้ว (Dr. ขาดทุน / Cr.
                              สต๊อก) — เกิดขึ้นเฉพาะตอนขาดเท่านั้น
                            </p>
                          </div>
                        ) : (
                          <div data-testid={`outsource-return-line-complete-${line.tag_code}`}>
                            <StatusBadge tone="success">รับครบ</StatusBadge>
                            <p className="cap-notice__note">ไม่มีผลทางบัญชี — ยอดรวมของโรงงานไม่เปลี่ยน</p>
                          </div>
                        )}
                      </td>
                      <td>
                        {discrepancy === null ? (
                          '—'
                        ) : (
                          <div data-testid={`outsource-discrepancy-${discrepancy.discrepancy_id}`}>
                            <StatusBadge tone={outsourceDiscrepancyStatusTone(discrepancy.status)}>
                              {outsourceDiscrepancyStatusLabel(discrepancy.status)}
                            </StatusBadge>
                            <p className="cap-notice__note">
                              คาดว่า {discrepancy.expected_qty} ได้จริง {discrepancy.actual_qty}
                            </p>
                            {outsourceDiscrepancyIsApprovable(discrepancy.status) ? (
                              <>
                                <button
                                  type="button"
                                  className="cap-button cap-button--primary"
                                  data-testid={`outsource-approve-discrepancy-${discrepancy.discrepancy_id}`}
                                  disabled={approvePending !== null || !canApprove}
                                  title={
                                    canApprove
                                      ? undefined
                                      : `ต้องมีสิทธิ์ ${OUTSOURCING_MODULE} + APPROVE จึงจะอนุมัติส่วนขาดได้`
                                  }
                                  onClick={() => {
                                    void runApproveDiscrepancy(discrepancy.discrepancy_id);
                                  }}
                                >
                                  {approvePending === discrepancy.discrepancy_id ? 'กำลังอนุมัติ…' : 'อนุมัติส่วนขาด'}
                                </button>
                                {canApprove ? null : (
                                  <span
                                    className="cap-field__hint"
                                    data-testid={`outsource-approve-reason-${discrepancy.discrepancy_id}`}
                                  >
                                    ต้องมีสิทธิ์ {OUTSOURCING_MODULE} + APPROVE
                                  </span>
                                )}
                              </>
                            ) : null}
                            {approveFailure?.discrepancyId === discrepancy.discrepancy_id ? (
                              <OutsourceFailureNotice
                                failure={approveFailure.failure}
                                context="approve"
                                testIdPrefix="outsource-approve"
                              />
                            ) : null}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ------------------------------------------------------- รายการ -- */}

      {items.status === 'loading' ? (
        <div className="cap-notice" data-testid="outsource-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายการใบจ้างผลิตภายนอก…</p>
        </div>
      ) : null}

      {items.status !== 'loading' && items.status !== 'ready' ? (
        <OutsourceFailureNotice
          failure={items}
          context="list"
          testIdPrefix="outsource-list"
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {items.status === 'ready' && listItems.length === 0 ? (
        <div className="cap-notice" data-testid="outsource-empty" role="status">
          <StatusBadge tone="neutral">ยังไม่มีใบจ้างผลิตภายนอก</StatusBadge>
        </div>
      ) : null}

      {items.status === 'ready' && listItems.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="ใบจ้างผลิตภายนอกทั้งหมด">
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="outsource-orders-table">
              <thead>
                <tr>
                  <th scope="col">เลขที่ใบ</th>
                  <th scope="col">โรงงาน</th>
                  <th scope="col">ผู้ขาย</th>
                  <th scope="col">กระบวนการ</th>
                  <th scope="col">สถานะ</th>
                  <th scope="col">บรรทัด</th>
                  <th scope="col">การดำเนินการ</th>
                </tr>
              </thead>
              <tbody>
                {listItems.map((order) => (
                  <tr
                    key={order.outsource_order_id}
                    data-testid={`outsource-row-${order.outsource_order_no}`}
                    data-status={order.status}
                  >
                    <th scope="row" className="cap-table__row-head">
                      <code className="cap-table__code">{order.outsource_order_no}</code>
                    </th>
                    <td>
                      <code className="cap-table__code">{order.plant_code}</code>
                    </td>
                    <td>
                      <code className="cap-table__code">{order.supplier_code}</code>
                    </td>
                    <td>
                      <code className="cap-table__code">{order.process_code}</code>
                    </td>
                    <td>
                      <StatusBadge tone={outsourceOrderStatusTone(order.status)}>
                        {outsourceOrderStatusLabel(order.status)}
                      </StatusBadge>
                    </td>
                    <td>{order.lines.length}</td>
                    <td>
                      {canAddOutsourceOrderLine(order) ? (
                        <span>
                          <button
                            type="button"
                            className="cap-button cap-button--ghost"
                            data-testid={`outsource-action-add-line-${order.outsource_order_no}`}
                            disabled={busy || !canEdit}
                            title={canEdit ? undefined : `ต้องมีสิทธิ์ ${OUTSOURCING_MODULE} + EDIT`}
                            onClick={() => {
                              openAddLine(order);
                            }}
                          >
                            เพิ่มบรรทัด
                          </button>
                        </span>
                      ) : null}

                      {canMarkOutsourceOrderReady(order) ? (
                        <span>
                          <button
                            type="button"
                            className="cap-button cap-button--ghost"
                            data-testid={`outsource-action-ready-${order.outsource_order_no}`}
                            disabled={busy || !canApprove || outsourceOrderHasNoLines(order)}
                            title={
                              !canApprove
                                ? `ต้องมีสิทธิ์ ${OUTSOURCING_MODULE} + APPROVE`
                                : outsourceOrderHasNoLines(order)
                                  ? 'ต้องมีอย่างน้อยหนึ่งบรรทัดก่อน'
                                  : undefined
                            }
                            onClick={() => {
                              void runMarkReady(order);
                            }}
                          >
                            {busyOrderId === order.outsource_order_id ? 'กำลังยืนยัน…' : 'ยืนยันพร้อมส่งออก'}
                          </button>
                          {!canApprove || outsourceOrderHasNoLines(order) ? (
                            <span
                              className="cap-field__hint"
                              data-testid={`outsource-action-ready-reason-${order.outsource_order_no}`}
                            >
                              {!canApprove
                                ? `ต้องมีสิทธิ์ ${OUTSOURCING_MODULE} + APPROVE`
                                : 'ต้องมีอย่างน้อยหนึ่งบรรทัดก่อน'}
                            </span>
                          ) : null}
                        </span>
                      ) : null}

                      {canDispatchOutsourceOrder(order) ? (
                        <span>
                          <button
                            type="button"
                            className="cap-button cap-button--ghost"
                            data-testid={`outsource-action-dispatch-${order.outsource_order_no}`}
                            disabled={busy || !canEdit}
                            title={canEdit ? undefined : `ต้องมีสิทธิ์ ${OUTSOURCING_MODULE} + EDIT`}
                            onClick={() => {
                              openDispatch(order);
                            }}
                          >
                            ส่งออก
                          </button>
                        </span>
                      ) : null}

                      {canReceiveOutsourceReturn(order) ? (
                        <span>
                          <button
                            type="button"
                            className="cap-button cap-button--ghost"
                            data-testid={`outsource-action-receive-${order.outsource_order_no}`}
                            disabled={busy || !canEdit}
                            title={canEdit ? undefined : `ต้องมีสิทธิ์ ${OUTSOURCING_MODULE} + EDIT`}
                            onClick={() => {
                              openReceive(order);
                            }}
                          >
                            รับกลับ
                          </button>
                        </span>
                      ) : null}

                      {readyFailure?.orderId === order.outsource_order_id ? (
                        <OutsourceFailureNotice
                          failure={readyFailure.failure}
                          context="ready"
                          testIdPrefix="outsource-ready"
                        />
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}
